import { AutocompleteInput } from '@/components/admin';
import { useGetList, useSimpleFormIterator, useSimpleFormIteratorItem } from 'ra-core';
import { useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

export const HSNAutocomplete = (props: any) => {
    const { source, onChange, arraySource, index, ...rest } = props;
    const { setValue } = useFormContext();
    const { data: hsnCodes } = useGetList('hsn_codes', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'code', order: 'ASC' },
    });

    const choices = hsnCodes || [];

    const handleChange = (value: any) => {
        if (onChange) {
            onChange(value);
        }
        
        if (value && arraySource !== undefined && index !== undefined) {
            const selectedHsn = choices.find((c: any) => c.code === value);
            if (selectedHsn) {
                 // Set window tracking flag to prevent the description popup from auto-opening
                 (window as any)._justAppliedHSNDesc = selectedHsn.description ?? '';

                 // only auto-populate description if there isn't one already to avoid overwriting typed descriptions
                 setValue(`${arraySource}.${index}.hsn_code`, selectedHsn.code, { shouldValidate: true, shouldDirty: true });
                 setValue(`${arraySource}.${index}.description`, selectedHsn.description ?? '', { shouldValidate: true, shouldDirty: true });
                 setValue(`${arraySource}.${index}.tax_rate`, selectedHsn.gst_rate ?? 0, { shouldValidate: true, shouldDirty: true });
                 setValue(`${arraySource}.${index}.unit`, selectedHsn.uqc ?? 'NOS', { shouldValidate: true, shouldDirty: true });
            }
        }
    };

    return (
        <AutocompleteInput
            source={source}
            choices={choices}
            optionText={(choice: any) =>
                choice?.code
                    ? `${choice.code}${choice.uqc ? ` [${choice.uqc}]` : ''}`
                    : ''
            }
            optionValue="code"
            inputText={(choice: any) => choice?.code || ''}
            matchSuggestion={(filter: string, choice: any) =>
                choice.code.toLowerCase().includes(filter.toLowerCase()) ||
                (choice.description || '').toLowerCase().includes(filter.toLowerCase())
            }
            suggestionLimit={15}
            onChange={handleChange}
            {...rest}
        />
    );
};

// ── HSN Suggestions Component ───────────────────────────────────────────────────
const HSNSuggestions = ({ arraySource, index }: { arraySource: string; index: number }) => {
    const { setValue, watch } = useFormContext();

    const descSource = `${arraySource}.${index}.description`;
    const hsnSource = `${arraySource}.${index}.hsn_code`;
    const taxSource = `${arraySource}.${index}.tax_rate`;
    const unitSource = `${arraySource}.${index}.unit`;

    // watch() must be called before useRef so the initial description is available.
    const description = watch(descSource);
    const hsnCode = watch(hsnSource);

    const [showSuggestions, setShowSuggestions] = useState(false);

    // Initialize with the description that is ALREADY in the form on mount.
    // This prevents the dropdown opening for rows that have pre-existing data
    // (e.g. when editing a saved record or when data is pre-populated).
    const closedForDescRef = useRef<string>(description ?? "");
    const hsnRef = useRef<string>(hsnCode ?? "");

    // Track whether the component has mounted yet.
    // The very first useEffect call should never open the dropdown.
    const isMountedRef = useRef<boolean>(false);

    // Fetch HSN codes (cached, extremely fast)
    const { data: hsnCodes } = useGetList('hsn_codes', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'code', order: 'ASC' },
    });

    const allCodes = hsnCodes || [];
    const searchedHSNs = allCodes.filter((hsn: any) => {
        if (!description || description.trim().length < 3) return false;
        const searchWord = description.trim().toLowerCase();
        return (hsn.description || '').toLowerCase().includes(searchWord) ||
               hsn.code.toLowerCase().includes(searchWord);
    }).slice(0, 10);

    const handleSelectHSN = (hsn: any) => {
        const desc = hsn.description ?? '';
        // Mark this description as "closed" — the effect below will NOT reopen for it
        closedForDescRef.current = desc;
        setShowSuggestions(false);
        setValue(hsnSource, hsn.code, { shouldValidate: true, shouldDirty: true });
        setValue(descSource, desc, { shouldValidate: true, shouldDirty: true });
        setValue(taxSource, hsn.gst_rate ?? 0, { shouldValidate: true, shouldDirty: true });
        setValue(unitSource, hsn.uqc ?? 'NOS', { shouldValidate: true, shouldDirty: true });
    };

    // Open suggestions only when the user is actively typing (not on mount, not after programmatic apply)
    useEffect(() => {
        const currentHsn = hsnCode ?? "";
        const prevHsn = hsnRef.current ?? "";
        const hsnChanged = currentHsn !== prevHsn;
        hsnRef.current = currentHsn;
        // Skip the very first effect run (component just mounted — user hasn't typed anything yet)
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            // Treat the initial description as "already seen" so it never reopens the dropdown
            closedForDescRef.current = description ?? "";
            return;
        }
        // If the description matches the window tracking flag (HSN code was selected),
        // treat it as already seen, clear the flag, and keep suggestions closed!
        if ((window as any)._justAppliedHSNDesc && (window as any)._justAppliedHSNDesc === description) {
            closedForDescRef.current = description ?? "";
            (window as any)._justAppliedHSNDesc = undefined;
            setShowSuggestions(false);
            return;
        }

        if (hsnChanged) {
            // If the HSN code was programmatically changed (e.g. via HSN autocomplete select),
            // prevent opening the description popover and treat the new description as already seen.
            closedForDescRef.current = description ?? "";
            setShowSuggestions(false);
            return;
        }
        if (!description || description.trim().length < 3) {
            setShowSuggestions(false);
            return;
        }
        // If description matches what we just auto-applied (or the initial value), keep closed
        if (description === closedForDescRef.current) {
            return;
        }
        // User typed something genuinely new — open the dropdown
        closedForDescRef.current = '';
        setShowSuggestions(true);
    }, [description, hsnCode, descSource]);

    // Only show suggestions if we have description and results and dropdown is open
    if (!showSuggestions || !description || description.trim().length < 3 || !searchedHSNs || searchedHSNs.length === 0) {
        return null;
    }

    return (
        <div className="absolute z-50 w-full bg-card border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto mt-1 top-full">
            <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b border-gray-100">
                Found {searchedHSNs.length} matching HSN codes
            </div>
            {searchedHSNs.map((hsn: any) => (
                <div
                    key={hsn.id}
                    className="px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    // Use onMouseDown so the event fires before the input loses focus,
                    // preventing any re-render / re-open race.
                    onMouseDown={(e) => { e.preventDefault(); handleSelectHSN(hsn); }}
                >
                    <div className="flex items-center justify-between">
                        <div>
                            <span className="font-mono font-semibold text-sm">{hsn.code}</span>
                            {hsn.uqc && <span className="ml-2 text-xs text-gray-500">[{hsn.uqc}]</span>}
                        </div>
                        <span className="text-xs text-green-600 font-medium">{hsn.gst_rate}% GST</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1 truncate">{hsn.description}</div>
                </div>
            ))}
        </div>
    );
};

// ── HSN Suggestions Wrapper ─────────────────────────────────────────────────────
const HSNSuggestionsWrapperInside = () => {
    const item = useSimpleFormIteratorItem();
    const iterator = useSimpleFormIterator();
    if (!item || !iterator) return null;
    return <HSNSuggestions arraySource={iterator.source} index={item.index} />;
};

export const HSNSuggestionsWrapper = () => {
    try {
        return <HSNSuggestionsWrapperInside />;
    } catch {
        return null;
    }
};

// ── HSNFieldWrapper ────────────────────────────────────────────────────────────
const HSNFieldWrapperInside = ({ label }: { label?: any }) => {
    const item = useSimpleFormIteratorItem();
    const iterator = useSimpleFormIterator();
    if (!item || !iterator) return null;
    return <HSNAutocomplete
        source="hsn_code"
        label={label !== undefined ? label : "HSN Code"}
        className="w-full"
        arraySource={iterator.source}
        index={item.index}
    />;
};

export const HSNFieldWrapper = ({ label, className }: { label?: any; className?: string }) => {
    const fallback = (
        <HSNAutocomplete
            source="hsn_code"
            label={label !== undefined ? label : "HSN Code"}
            className={className}
        />
    );
    try {
        return <HSNFieldWrapperInside label={label} />;
    } catch {
        return fallback;
    }
};